<template>
  <div class="payment-methods-page">
    <!-- PAGE HERO -->
    <DeliveryHero
      :title="heading"
      subtitle="We offer a variety of secure payment options so you can check out your way."
    />

    <!-- MAIN LAYOUT -->
    <div class="main-layout">
      <!-- Sidebar -->
      <DeliverySidebar
        ref="sidebar"
        :sections="sections"
        :active-id="activeId"
        @navigate="scrollToSection"
      />

      <!-- Content -->
      <div class="policy-content">
        <!-- Trust intro -->
        <p class="policy-intro reveal">
          All transactions on Hooligans Eyewear are processed securely. We never
          store your full card details — payments are handled by our
          PCI-compliant payment partners.
        </p>

        <!-- ── Credit & Debit Cards ──────────────────────────── -->
        <PolicySection
          class="reveal"
          section-id="cards"
          eyebrow="Standard"
          title="Credit &amp; Debit Cards"
        >
          <p>
            We accept all major credit and debit cards. Your billing address
            must match the address on file with your card issuer.
          </p>

          <!-- Card logos -->
          <div class="card-logos">
            <a
              v-for="card in cardBrands"
              :key="card.name"
              :href="card.url"
              target="_blank"
              rel="noopener noreferrer"
              class="card-logo"
            >
              <img :src="card.icon" :alt="card.name" />
            </a>
          </div>

          <div class="payment-methods-list">
            <PaymentMethodRow
              name="Credit Card"
              description="Visa, Mastercard, American Express, Discover"
              badge-type="instant"
              badge-text="INSTANT"
              icon-style="background:#f0f0f8; border:1px solid #e0e0e0;"
            >
              <template #icon>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#10069f"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect x="2" y="5" width="20" height="14" rx="2" />
                  <line x1="2" y1="10" x2="22" y2="10" />
                </svg>
              </template>
            </PaymentMethodRow>

            <PaymentMethodRow
              name="Debit Card"
              description="All major debit cards with Visa or Mastercard logo"
              badge-type="instant"
              badge-text="INSTANT"
              icon-style="background:#f0f0f8; border:1px solid #e0e0e0;"
            >
              <template #icon>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#10069f"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect x="2" y="5" width="20" height="14" rx="2" />
                  <line x1="2" y1="10" x2="22" y2="10" />
                  <circle cx="7" cy="15" r="1.5" fill="#10069f" stroke="none" />
                </svg>
              </template>
            </PaymentMethodRow>
          </div>
        </PolicySection>

        <!-- ── Digital Wallets ───────────────────────────────── -->
        <PolicySection
          class="reveal"
          section-id="wallets"
          eyebrow="Wallets"
          title="Digital Wallets"
        >
          <p>
            Check out faster using your preferred digital wallet. No need to
            enter card details manually.
          </p>

          <div class="payment-methods-list">
            <PaymentMethodRow
              v-for="w in wallets"
              :key="w.name"
              :name="w.name"
              :description="w.description"
              :url="w.url"
              badge-type="instant"
              badge-text="INSTANT"
              icon-style="background:#fff; border:1px solid #e0e0e0; padding:6px;"
            >
              <template #icon>
                <img
                  :src="w.icon"
                  :alt="w.name"
                  style="width: 100%; height: 100%; object-fit: contain"
                />
              </template>
            </PaymentMethodRow>
          </div>
        </PolicySection>

        <!-- ── Buy Now, Pay Later ────────────────────────────── -->
        <PolicySection
          class="reveal"
          section-id="bnpl"
          eyebrow="Buy Now, Pay Later"
          title="Flexible Payment Plans"
        >
          <p>
            Split your purchase into smaller installments at no extra cost.
            Available at checkout — subject to approval by the provider.
          </p>

          <div class="payment-methods-list">
            <PaymentMethodRow
              v-for="b in bnplProviders"
              :key="b.name"
              :name="b.name"
              :description="b.description"
              :url="b.url"
              badge-type="bnpl"
              badge-text="BNPL"
              :icon-style="b.iconStyle"
            >
              <template #icon>
                <img
                  :src="b.icon"
                  :alt="b.name"
                  style="width: 100%; height: 100%; object-fit: contain"
                />
              </template>
            </PaymentMethodRow>
          </div>

          <div class="bnpl-callout">
            <p>
              <strong>Note:</strong> Buy Now, Pay Later options are provided by
              third-party lenders. Availability and approval are subject to the
              provider's terms and your creditworthiness. Hooligans Eyewear is
              not responsible for the terms set by these providers.
            </p>
          </div>
        </PolicySection>

        <!-- ── Secure Checkout ───────────────────────────────── -->
        <PolicySection
          class="reveal"
          section-id="security"
          eyebrow="Safety"
          title="Secure Checkout"
        >
          <p>
            All payment information is encrypted using TLS (Transport Layer
            Security). We are PCI DSS compliant and never store your full card
            number on our servers.
          </p>
          <p>
            If you have any concerns about a transaction or suspect fraudulent
            activity, please contact us immediately.
          </p>
          <CalloutBox>
            Questions about a charge? Email us at
            <a href="mailto:support@hooliganseyewear.com" class="email-link"
              >support@hooliganseyewear.com</a
            >
            and we'll help resolve it promptly.
          </CalloutBox>
        </PolicySection>

        <!-- Contact CTA -->
        <ContactCta
          class="reveal"
          heading="Need help at checkout?"
          description="Our team is happy to assist with any payment questions or issues."
        />
      </div>
    </div>
  </div>
</template>

<script>
import "./payment_methods.css";

import DeliveryHero from "~/components/delivery/DeliveryHero.vue";
import DeliverySidebar from "~/components/delivery/DeliverySidebar.vue";
import PolicySection from "~/components/delivery/PolicySection.vue";
import ContactCta from "~/components/delivery/ContactCta.vue";
import CalloutBox from "~/components/ui/CalloutBox.vue";
import PaymentMethodRow from "~/components/payment-methods/PaymentMethodRow.vue";
import { cdnUrl } from "~/utils/cdn";
import { useStaticPageSeo } from "~/core/composables/useStaticPageSeo";

export default {
  name: "PaymentMethodsPage",
  setup() {
    return useStaticPageSeo({
      title: "Payment Methods | Hooligans Eyewear",
      heading: "Payment Methods",
      description:
        "View accepted payment methods at Hooligans Eyewear, including major cards and available buy now, pay later options at checkout.",
    });
  },
  components: {
    DeliveryHero,
    DeliverySidebar,
    PolicySection,
    ContactCta,
    CalloutBox,
    PaymentMethodRow,
  },
  data() {
    return {
      activeId: "cards",
      sections: [
        { id: "cards", long: "Credit & Debit Cards" },
        { id: "wallets", long: "Digital Wallets" },
        { id: "bnpl", long: "Buy Now, Pay Later" },
        { id: "security", long: "Security" },
      ],
      cardBrands: [
        {
          name: "Visa",
          icon: "/assets/payment-icons/visa.png",
          url: "https://www.visa.com",
        },
        {
          name: "Mastercard",
          icon: "/assets/payment-icons/mastercard.png",
          url: "https://www.mastercard.com",
        },
        {
          name: "American Express",
          icon: "/assets/payment-icons/amex.png",
          url: "https://www.americanexpress.com",
        },
        {
          name: "Discover",
          icon: "/assets/payment-icons/discover.png",
          url: "https://www.discover.com",
        },
      ],
      wallets: [
        {
          name: "Google Pay",
          description: "Pay with cards saved to your Google account",
          icon: "/assets/payment-icons/googlepay.png",
          url: "https://pay.google.com",
        },
        {
          name: "Amazon Pay",
          description: "Use the payment methods saved in your Amazon account",
          icon: "/assets/payment-icons/amazonpay.png",
          url: "https://pay.amazon.com",
        },
      ],
      bnplProviders: [
        {
          name: "Klarna",
          description: "Pay in 4 interest-free installments, or pay in 30 days",
          icon: "/assets/payment-icons/klarna.png",
          url: "https://www.klarna.com",
          iconStyle: "background:#FFB3C7; padding:6px;",
        },
        {
          name: "Afterpay",
          description:
            "4 fortnightly interest-free payments, automatically deducted",
          icon: "/assets/payment-icons/afterpay.png",
          url: "https://www.afterpay.com",
          iconStyle: "background:#B2FCE4; padding:6px;",
        },
        {
          name: "Cash App Pay",
          description: "Pay directly from your Cash App balance or linked card",
          icon: "/assets/payment-icons/cashapp.png",
          url: "https://cash.app",
          iconStyle: "background:#00D64F; padding:6px;",
        },
        {
          name: "Affirm",
          description:
            "Monthly payment plans from 3 to 36 months — no hidden fees",
          icon: "/assets/payment-icons/affirm.png",
          url: "https://www.affirm.com",
          iconStyle: "background:#fff; border:1px solid #e8e8e8; padding:6px;",
        },
      ],
    };
  },
  mounted() {
    if (typeof window === "undefined") return;

    this._reduceMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    this._setupIntersectionObserver();
    this._setupRevealObserver();
  },
  beforeUnmount() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
    if (this._revealObserver) {
      this._revealObserver.disconnect();
      this._revealObserver = null;
    }
  },
  methods: {
    _getClickOffset() {
      if (typeof window === "undefined") return 24;
      return window.innerWidth <= 900 ? 80 : 24;
    },
    _setupRevealObserver() {
      const els = this.$el.querySelectorAll(".reveal");
      if (!els.length) return;

      if (this._reduceMotion || typeof IntersectionObserver === "undefined") {
        els.forEach((el) => el.classList.add("visible"));
        return;
      }

      this._revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("visible");
              this._revealObserver.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
      );
      els.forEach((el) => this._revealObserver.observe(el));
    },
    _setupIntersectionObserver() {
      const sectionIds = this.sections.map((s) => s.id);
      const els = sectionIds
        .map((id) => document.getElementById(id))
        .filter(Boolean);

      if (!els.length) return;

      this._observer = new IntersectionObserver(
        (entries) => {
          // Track which sections are currently intersecting
          for (const entry of entries) {
            const id = entry.target.id;
            if (!this._visibleSections) this._visibleSections = new Set();
            if (entry.isIntersecting) {
              this._visibleSections.add(id);
            } else {
              this._visibleSections.delete(id);
            }
          }

          // Pick the first visible section in document order
          for (const id of sectionIds) {
            if (this._visibleSections && this._visibleSections.has(id)) {
              if (this.activeId !== id) {
                this.activeId = id;
              }
              break;
            }
          }
        },
        {
          rootMargin: "-80px 0px -60% 0px",
          threshold: 0,
        }
      );

      for (const el of els) {
        this._observer.observe(el);
      }
    },
    scrollToSection(id) {
      if (typeof window === "undefined") return;
      const target = document.getElementById(id);
      if (!target) return;
      const offset = this._getClickOffset();
      const top = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
      this.activeId = id;
    },
  },
};
</script>
